/**
 * DashboardView — /dashboards/:id
 * One dashboard's widgets on the DashboardPage template. Demo pages: /analytics and /saas (widget cards in a grid
 * under the page header). Template example: NextAdmin `(dashboard)/(home)/page.tsx`. Every edit control is gated
 * (`CanManageDashboards`) and system dashboards are read-only.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, LayoutGrid, MessageSquare } from 'lucide-react';
import {
  addWidgetMutation,
  deleteDashboardMutation,
  getDashboardOptions,
  getDashboardQueryKey,
  listDashboardsQueryKey,
  removeWidgetMutation,
  reorderWidgetsMutation,
  updateDashboardMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { DashboardGrid, DashboardPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { EntityAssistantDrawer } from '../../components/assistant/EntityAssistantDrawer.js';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { Card } from '../../components/ui/Card.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useToast } from '../../components/ui/Toast.js';
import { WidgetPicker } from './WidgetPicker.js';
import { WidgetCard } from './WidgetCard.js';
import { DashboardHeader } from './DashboardHeader.js';

const GridSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3" role="status" aria-label="Loading dashboard">
    {[0, 1, 2].map((i) => (
      <Card key={i} className="space-y-3">
        <Skeleton height={14} width="40%" />
        <Skeleton height={120} rounded="rounded-lg" />
      </Card>
    ))}
  </div>
);

/** `id` shows that dashboard without a route (a conversation's side panel); the route's `:id` otherwise. */
export const DashboardView: React.FC<{ id?: string }> = ({ id: idProp }) => {
  const params = useParams<{ id: string }>();
  const id = idProp ?? params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [editMode, setEditMode] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const query = useQuery(getDashboardOptions({ path: { id } }));
  const dashboard = query.data?.dashboard;
  // System (H1) dashboards are read-only: no edit mode, add, reorder or delete.
  const isSystem = dashboard?.ownerType === 'system';
  const editing = editMode && !isSystem;

  const refresh = () => queryClient.invalidateQueries({ queryKey: getDashboardQueryKey({ path: { id } }) });
  const fail = (error: Error) => notify(error.message, 'danger');

  const add = useMutation({
    ...addWidgetMutation(),
    onSuccess: async () => {
      await refresh();
      setPickerOpen(false);
      notify('Widget added.');
    },
    onError: fail,
  });
  const remove = useMutation({ ...removeWidgetMutation(), onSuccess: refresh, onError: fail });
  const reorder = useMutation({ ...reorderWidgetsMutation(), onSuccess: refresh, onError: fail });
  const update = useMutation({ ...updateDashboardMutation(), onSuccess: refresh, onError: fail });
  const del = useMutation({
    ...deleteDashboardMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: listDashboardsQueryKey() });
      notify('Dashboard deleted.');
      navigate('/dashboards');
    },
    onError: fail,
  });

  if (query.isPending) {
    return (
      <DashboardPage title="Dashboard">
        <GridSkeleton />
      </DashboardPage>
    );
  }

  if (query.isError || !dashboard) {
    return (
      <DashboardPage title="Dashboard">
        <Callout
          tone="danger"
          icon={AlertTriangle}
          title="Could not load this dashboard"
          actions={
            <Button size="sm" variant="secondary" onClick={() => navigate('/dashboards')}>
              Back to dashboards
            </Button>
          }
        >
          {query.error?.message ?? 'It may have been deleted, or you may not have access to it.'}
        </Callout>
      </DashboardPage>
    );
  }

  const widgets = dashboard.widgets;
  // Swap a widget's position with its neighbour's.
  const move = (index: number, delta: -1 | 1) => {
    const neighbour = widgets[index + delta];
    const current = widgets[index];
    if (!neighbour || !current) return;
    reorder.mutate({
      path: { id },
      body: {
        widgets: widgets.map((w) => ({
          id: w.id,
          position: w.id === current.id ? neighbour.position : w.id === neighbour.id ? current.position : w.position,
        })),
      },
    });
  };

  return (
    <>
      <DashboardPage
        title={dashboard.name}
        subtitle={dashboard.description ?? undefined}
        breadcrumbs={[{ label: 'Dashboards', to: '/dashboards' }]}
        actions={
          <>
            {/* The dashboard's assistant (TA desk U9): answers from what the widgets show; the server names its
                persona — the technical desk's dashboard opens on its Desk Lead. */}
            {/* The create route admits either policy (`createConversation`). */}
            <Can policies={['CanConverse', 'CanManageAgents']} mode="any">
              <EntityAssistantDrawer
                subjectType="dashboard"
                subjectId={String(dashboard.id)}
                draftKey={`dashboard-${dashboard.id}`}
                entryPoint="dashboard"
                conversationKind="user-agent"
                currentFormValues={{}}
                contextExtras={{ dashboard: { dashboardId: String(dashboard.id) } }}
                trigger={(open) => (
                  <Button variant="secondary" size="sm" onClick={open}>
                    <MessageSquare size={14} aria-hidden="true" />
                    Ask
                  </Button>
                )}
              />
            </Can>
            <DashboardHeader
              dashboard={dashboard}
              isSystem={isSystem}
              editMode={editing}
              onToggleEditMode={() => setEditMode(!editMode)}
              onAddWidget={() => setPickerOpen(true)}
              onRename={(values) => update.mutateAsync({ path: { id }, body: values })}
              onDeleteDashboard={() => del.mutate({ path: { id } })}
              deleting={del.isPending}
            />
          </>
        }
      >
        {widgets.length === 0 ? (
          <Card>
            <EmptyState
              icon={LayoutGrid}
              title="No widgets yet"
              description="Add a widget to show workflow outputs or metrics here."
              action={
                !isSystem ? (
                  <Can policy="CanManageDashboards">
                    <Button
                      onClick={() => {
                        setEditMode(true);
                        setPickerOpen(true);
                      }}
                    >
                      Add your first widget
                    </Button>
                  </Can>
                ) : undefined
              }
            />
          </Card>
        ) : (
          <DashboardGrid className="xl:grid-cols-3">
            {widgets.map((widget, index) => (
              <div key={widget.id}>
                <WidgetCard
                  widget={widget}
                  editMode={editing}
                  isFirst={index === 0}
                  isLast={index === widgets.length - 1}
                  onMoveUp={() => move(index, -1)}
                  onMoveDown={() => move(index, 1)}
                  onRemove={() => remove.mutate({ path: { id, widgetId: String(widget.id) } })}
                />
              </div>
            ))}
          </DashboardGrid>
        )}
      </DashboardPage>

      <Can policy="CanManageDashboards">
        <WidgetPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onAdd={(widgetDefinitionId) =>
            add.mutate({ path: { id }, body: { widgetDefinitionId, position: widgets.length + 1 } })
          }
          adding={add.isPending}
        />
      </Can>
    </>
  );
};
